'use client';

import { formatDateKey } from '@/lib/date';
import { formatCompactMoney, formatMoney } from '@/lib/money';
import { recurringDateForMonth } from '@/lib/recurring';
import type { Category, RecurringExpense, Transaction } from '@/lib/types';

const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export default function CalendarMonthView({
  transactions,
  recurringExpenses,
  selectedDate,
  onSelectDate,
  currency,
}: {
  transactions: Transaction[];
  categories: Category[];
  recurringExpenses: RecurringExpense[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
  currency: string;
}) {
  const today = new Date();
  const month = today.getMonth();
  const year = today.getFullYear();
  const firstDay = new Date(year, month, 1);
  const startIndex = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(today);

  const grid = Array.from({ length: startIndex + daysInMonth }, (_, index) => {
    if (index < startIndex) return null;
    const dayNumber = index - startIndex + 1;
    const date = new Date(year, month, dayNumber);
    const dateKey = formatDateKey(date);
    const dayTransactions = transactions.filter((tx) => tx.date === dateKey);
    const dayRecurringExpenses = recurringExpenses.filter(
      (item) => item.active && recurringDateForMonth(item, year, month) === dateKey
    );
    const income = dayTransactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0);
    const expense =
      dayTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0) +
      dayRecurringExpenses.reduce((sum, item) => sum + item.amount, 0);

    return {
      dayNumber,
      dateKey,
      income,
      expense,
      balance: income - expense,
      hasActivity: dayTransactions.length > 0 || dayRecurringExpenses.length > 0,
    };
  });

  const monthIncome = grid.reduce((sum, cell) => sum + (cell?.income ?? 0), 0);
  const monthExpense = grid.reduce((sum, cell) => sum + (cell?.expense ?? 0), 0);
  const monthBalance = monthIncome - monthExpense;
  const activeDays = grid.filter((cell) => cell?.hasActivity).length;

  const describeDay = (cell: NonNullable<(typeof grid)[number]>) => {
    const dateLabel = new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(new Date(cell.dateKey));

    if (!cell.hasActivity) {
      return `${dateLabel}, aucune opération`;
    }

    return `${dateLabel}, entrées ${formatMoney(cell.income, currency, 0)}, sorties ${formatMoney(cell.expense, currency, 0)}, solde ${formatMoney(cell.balance, currency, 0)}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-lg font-semibold capitalize text-slate-100">{monthLabel}</p>
          <p className="mt-1 text-sm text-slate-400">{formatMoney(monthBalance, currency, 0)} sur le mois</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-400">
          <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-emerald-300">
            +{formatCompactMoney(monthIncome, currency)}
          </span>
          <span className="rounded-full bg-rose-500/10 px-2 py-1 text-rose-300">
            -{formatCompactMoney(monthExpense, currency)}
          </span>
          <span className="rounded-full bg-slate-800 px-2 py-1 text-slate-200">{activeDays} jours</span>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-950 p-2">
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase text-slate-400 sm:gap-2">
          {DAY_NAMES.map((day) => (
            <div key={day} className="py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-7 gap-1 sm:gap-2">
          {grid.map((cell, cellIndex) =>
            cell ? (
              <button
                key={cell.dateKey}
                type="button"
                onClick={() => onSelectDate(cell.dateKey)}
                aria-label={describeDay(cell)}
                aria-pressed={selectedDate === cell.dateKey}
                className={`min-h-14 rounded-xl border p-1 text-left shadow-none transition sm:min-h-28 sm:rounded-2xl sm:p-2 ${
                  selectedDate === cell.dateKey
                    ? 'border-brand-500 bg-brand-500/15 ring-1 ring-brand-500/40'
                    : cell.hasActivity
                      ? 'border-slate-700 bg-slate-900 hover:border-slate-600 hover:bg-slate-800'
                      : 'border-slate-800 bg-slate-950/70 hover:bg-slate-900'
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-sm font-semibold text-slate-100">{cell.dayNumber}</span>
                  {cell.hasActivity ? <span className="h-2 w-2 shrink-0 rounded-full bg-brand-400" /> : null}
                </div>

                {cell.hasActivity ? (
                  <div className="mt-2 space-y-1 text-xs leading-tight">
                    <div className="flex items-center gap-1 sm:hidden">
                      {cell.income > 0 ? <span className="h-1.5 flex-1 rounded-full bg-emerald-400" /> : null}
                      {cell.expense > 0 ? <span className="h-1.5 flex-1 rounded-full bg-rose-400" /> : null}
                    </div>
                    <div
                      className={`truncate rounded-lg px-1 py-0.5 text-center font-bold sm:hidden ${
                        cell.balance >= 0 ? 'bg-emerald-500/10 text-emerald-200' : 'bg-rose-500/10 text-rose-200'
                      }`}
                    >
                      {cell.balance >= 0 ? '+' : '-'}{formatCompactMoney(cell.balance, currency)}
                    </div>

                    <div className="hidden items-center justify-between gap-1 text-emerald-400 sm:flex">
                      <span className="font-semibold">+</span>
                      <span className="truncate font-medium">{formatMoney(Math.abs(cell.income), currency, 0)}</span>
                    </div>
                    <div className="hidden items-center justify-between gap-1 text-rose-400 sm:flex">
                      <span className="font-semibold">-</span>
                      <span className="truncate font-medium">{formatMoney(Math.abs(cell.expense), currency, 0)}</span>
                    </div>
                    <div
                      className={`mt-1 hidden truncate rounded-xl px-1.5 py-1 text-center font-bold sm:block ${
                        cell.balance >= 0 ? 'bg-emerald-500/10 text-emerald-200' : 'bg-rose-500/10 text-rose-200'
                      }`}
                    >
                      {cell.balance >= 0 ? '+' : '-'}{formatMoney(Math.abs(cell.balance), currency, 0)}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 h-px w-4 rounded-full bg-slate-800 sm:mt-5 sm:w-5" />
                )}
              </button>
            ) : (
              <div key={`empty-${cellIndex}`} className="min-h-14 rounded-xl bg-transparent sm:min-h-28 sm:rounded-2xl" />
            )
          )}
        </div>
      </div>
    </div>
  );
}
